import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { fetchProjektDokumente } from '../../services/projektDokumenteService';
import { ProjektDokument } from './types';

/**
 * Das Laden der Projektablage — der einzige Zustand, den der Container selbst
 * haelt.
 *
 * Bewusst ein Hook und keine Logik in der Komponente: Filtern, Sortieren und
 * Gruppieren stehen als pure Funktionen daneben in `dokumenteLogic.ts` und
 * sind ohne DOM pruefbar; hier bleibt nur das Nachladen.
 *
 * Geladen wird IMMER die ganze Projektablage, auch in der eingebetteten
 * Modulansicht: die Leiste im SiGe-Plan zeigt zwar nur die zugeordneten
 * Dokumente, aber sie bietet daneben an, weitere aus der Ablage zuzuordnen —
 * dafuer braucht sie ohnehin alles. Eine zweite, engere Abfrage waere ein
 * zweiter Weg zu denselben Daten und damit eine Stelle mehr, an der die
 * Anzeige auseinanderlaufen kann.
 */
export const useDokumente = (projectId: string | null | undefined) => {
  const [dokumente, setDokumente] = useState<ProjektDokument[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  /**
   * Laufmarke gegen zwei verschiedene Fehler mit derselben Ursache: eine
   * Antwort, die zu spaet kommt.
   *
   * (1) Wer zweimal schnell auf „Neu laden" klickt, hat zwei Abfragen
   *     unterwegs — ohne Marke kann die AELTERE Antwort die neuere
   *     ueberschreiben, und die Liste zeigt einen Stand, den niemand angefragt
   *     hat.
   * (2) Wird das Panel waehrend des Ladens geschlossen, liefe das setState in
   *     eine abgebaute Komponente.
   *
   * Ein Zaehler statt eines Boolean: er beantwortet beide Fragen mit einem
   * Vergleich („bin ich noch der juengste Lauf?").
   */
  const lauf = useRef(0);
  useEffect(() => () => { lauf.current += 1; }, []);

  const neuLaden = useCallback(async () => {
    const meiner = (lauf.current += 1);
    if (!projectId) {
      setDokumente([]);
      setLaden(false);
      return;
    }
    setLaden(true);
    setFehler(null);
    try {
      const liste = await fetchProjektDokumente(projectId);
      if (lauf.current !== meiner) return;
      setDokumente(liste);
    } catch (e) {
      if (lauf.current !== meiner) return;
      setFehler(describeError(e));
    } finally {
      if (lauf.current === meiner) setLaden(false);
    }
  }, [projectId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  /**
   * Nimmt ein geaendertes Dokument in die Liste auf — vorhandenes wird
   * ersetzt, neues vorn eingereiht.
   *
   * WARUM nicht einfach neu laden: die Antwort des Servers steht schon im
   * Ergebnis des Schreibens, und auf der Baustelle (schmale Leitung) ist jede
   * vermiedene Abfrage spuerbar.
   */
  const uebernehmen = useCallback((geaendert: ProjektDokument) => {
    setDokumente(prev => {
      const vorhanden = prev.some(d => d.id === geaendert.id);
      return vorhanden
        ? prev.map(d => (d.id === geaendert.id ? geaendert : d))
        : [geaendert, ...prev];
    });
  }, []);

  const entfernen = useCallback((id: string) => {
    setDokumente(prev => prev.filter(d => d.id !== id));
  }, []);

  return { dokumente, laden, fehler, neuLaden, uebernehmen, entfernen };
};
